import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'

import ColetaClickstream from '../../../components/clickstreamAnalise/clickstreamColeta.js'
import { api_gateway } from '../../../conexoes/api.js'
import {
  filtrosArrayBarra,
  mudarFiltros,
  realizouFiltro,
  recarregarBarraFiltragem,
  revelarBarraFiltragem
} from '../../../redux/actions'
import { useGlobal } from '../../../stores/useGlobal.js'
import { formatDateToDatePicker } from '../../../utils/genericFormatters'
import DatePicker from '../../Mantine/DatePicker'
import Input from '../../Mantine/Input'
import Modal from '../../Mantine/Modal'
import MultiSelect from '../../Mantine/MultiSelect'

import * as S from './styles'

const capitalizeText = (text = '') =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()

const DEFAULT_FILTERS_VALUES = {
  emissaoInicio: null,
  emissaoFinal: null,
  envioInicio: null,
  envioFinal: null,
  operacao: [],
  valorMinimo: '',
  valorMaximo: ''
}

const temAlgumFiltroPreenchido = (filters) =>
  Boolean(filters.emissaoInicio) ||
  Boolean(filters.emissaoFinal) ||
  Boolean(filters.envioInicio) ||
  Boolean(filters.envioFinal) ||
  filters.operacao?.length > 0 ||
  Boolean(filters.valorMinimo) ||
  Boolean(filters.valorMaximo)

const converterValor = (valor) => {
  if (!valor || valor === '') return ''

  if (valor.includes(',')) return valor

  const numero = parseFloat(valor)
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatarValor = (valor) => {
  const apenasNumeros = valor.replace(/[^\d]/g, '')

  if (apenasNumeros === '') return ''

  const numeroEmCentavos = parseInt(apenasNumeros, 10)
  const reais = Math.floor(numeroEmCentavos / 100)
  const centavos = numeroEmCentavos % 100
  const reaisFormatados = reais.toLocaleString('pt-BR')
  const centavosFormatados = centavos.toString().padStart(2, '0')

  return `${reaisFormatados},${centavosFormatados}`
}

const valorParaApi = (valor) =>
  valor ? valor.replace(/\./g, '').replace(',', '.') : undefined

const API_DATE_FORMAT = 'yyyy-MM-dd hh:mm:ss'
const FILTER_BAR_DATE_FORMAT = 'dd-MM-yyyy'

const formatDateForApi = (date) => format(date, API_DATE_FORMAT)
const formatDateForBarra = (date) => format(date, FILTER_BAR_DATE_FORMAT)

const LABELS_INTERVALO_DATA = {
  apenasInicio: 'Período a partir de',
  apenasFim: 'Período até',
  entre: 'entre'
}

const LABELS_INTERVALO_VALOR = {
  apenasInicio: 'a partir de',
  apenasFim: 'até',
  entre: 'entre'
}

const montarLabelIntervalo = ({
  titulo,
  inicio,
  fim,
  rotulos,
  formatar = (valor) => valor,
  chaveInicio,
  chaveFim,
  chaveIntervalo
}) => {
  const temInicio = Boolean(inicio)
  const temFim = Boolean(fim)

  if (!temInicio && !temFim) return null

  if (temInicio && !temFim) {
    return `${titulo}: ${rotulos.apenasInicio} ${formatar(inicio)}?${chaveInicio}`
  }

  if (!temInicio && temFim) {
    return `${titulo}: ${rotulos.apenasFim} ${formatar(fim)}?${chaveFim}`
  }

  return `${titulo}: ${rotulos.entre} ${formatar(inicio)} e ${formatar(fim)}?${chaveIntervalo}`
}

const CONFIG_INTERVALOS_BARRA = [
  {
    titulo: 'Emissão',
    inicioKey: 'emissaoInicio',
    fimKey: 'emissaoFinal',
    rotulos: LABELS_INTERVALO_DATA,
    formatar: formatDateForBarra,
    chaveInicio: 'EMISSAO_INICIO',
    chaveFim: 'EMISSAO_FINAL',
    chaveIntervalo: 'EMISSAO_INICIO'
  },
  {
    titulo: 'Envio',
    inicioKey: 'envioInicio',
    fimKey: 'envioFinal',
    rotulos: LABELS_INTERVALO_DATA,
    formatar: formatDateForBarra,
    chaveInicio: 'ENVIO_INICIO',
    chaveFim: 'ENVIO_FINAL',
    chaveIntervalo: 'ENVIO_INICIO'
  },
  {
    titulo: 'Valor',
    inicioKey: 'valorMinimo',
    fimKey: 'valorMaximo',
    rotulos: LABELS_INTERVALO_VALOR,
    chaveInicio: 'VALOR_MIN',
    chaveFim: 'VALOR_MAX',
    chaveIntervalo: 'VALOR_MIN'
  }
]

const montarArrayFiltrosBarra = (filters, tiposPedidosOptions = []) => {
  const filtrosBarra = CONFIG_INTERVALOS_BARRA.map(
    ({ inicioKey, fimKey, ...config }) =>
      montarLabelIntervalo({
        ...config,
        inicio: filters[inicioKey],
        fim: filters[fimKey]
      })
  ).filter(Boolean)

  if (filters.operacao?.length > 0) {
    const nomesOperacao = filters.operacao
      .map(
        (id) =>
          tiposPedidosOptions.find((tipo) => tipo.value === id)?.label
      )
      .filter(Boolean)
      .join(', ')

    if (nomesOperacao) {
      filtrosBarra.push(`Tipo: ${nomesOperacao}?OPERACAO`)
    }
  }

  return filtrosBarra
}

/**
 * Modal de filtros do módulo Pedidos Simcard.
 * @param {{ opened: boolean, close: () => void }} props
 */
export default function FiltrosPedidosSimcard({ opened, close }) {
  const { setMostrarNotificacao } = useGlobal()
  const [localFilters, setLocalFilters] = useState({
    ...DEFAULT_FILTERS_VALUES
  })
  const [formResetKey, setFormResetKey] = useState(0)

  const despacho = useDispatch()
  const filtros = useSelector((state) => state.filtrosState.filtros)
  const recarregarBarraFiltragemValor = useSelector(
    (state) => state.filtrosState.recarregarBarraFiltragem
  )

  const { data: tiposPedidos } = useQuery(
    ['tipos-pedidos'],
    async () => await api_gateway.get('/m1/consultar/pedido_tipo'),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onError: () => {
        setMostrarNotificacao({
          show: true,
          type: 'error',
          title: 'Erro',
          message: 'Não foi possível carregar os tipos de pedido.'
        })
      }
    }

  )

  const tiposPedidosOptions =
    tiposPedidos?.data?.dados?.map((tipo) => ({
      value: tipo.id,
      label: capitalizeText(tipo.label)
    }))

  useEffect(() => {
    if (!filtros?.vazio) {
      const auxFilter = { ...DEFAULT_FILTERS_VALUES }

      if (filtros.EMISSAO_INICIO) {
        auxFilter.emissaoInicio = formatDateToDatePicker(
          new Date(filtros.EMISSAO_INICIO)
        )
      }
      if (filtros.EMISSAO_FINAL) {
        auxFilter.emissaoFinal = formatDateToDatePicker(
          new Date(filtros.EMISSAO_FINAL)
        )
      }
      if (filtros.ENVIO_INICIO) {
        auxFilter.envioInicio = formatDateToDatePicker(
          new Date(filtros.ENVIO_INICIO)
        )
      }
      if (filtros.ENVIO_FINAL) {
        auxFilter.envioFinal = formatDateToDatePicker(
          new Date(filtros.ENVIO_FINAL)
        )
      }
      if (filtros.OPERACAO) {
        auxFilter.operacao = filtros.OPERACAO
      }
      if (filtros.VALOR_MIN) {
        auxFilter.valorMinimo = converterValor(filtros.VALOR_MIN)
      }
      if (filtros.VALOR_MAX) {
        auxFilter.valorMaximo = converterValor(filtros.VALOR_MAX)
      }

      setLocalFilters(auxFilter)
    }
  }, [filtros])

  useEffect(() => {
    if (
      localFilters.emissaoFinal &&
      localFilters.emissaoInicio &&
      localFilters.emissaoFinal < localFilters.emissaoInicio
    ) {
      setLocalFilters((prev) => ({ ...prev, emissaoFinal: null }))
    }
  }, [localFilters.emissaoInicio, localFilters.emissaoFinal])

  useEffect(() => {
    if (
      localFilters.envioFinal &&
      localFilters.envioInicio &&
      localFilters.envioFinal < localFilters.envioInicio
    ) {
      setLocalFilters((prev) => ({ ...prev, envioFinal: null }))
    }
  }, [localFilters.envioInicio, localFilters.envioFinal])

  const handleLocalFiltroChange = (field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleValorChange = (field, valor) => {
    const filtro = /^[0-9.,]*$/
    if (valor === '' || filtro.test(valor)) {
      handleLocalFiltroChange(field, formatarValor(valor))
    }
  }

  const formatFiltersToApply = () => {
    const filters = {
      ...(localFilters.emissaoInicio && {
        EMISSAO_INICIO: formatDateForApi(localFilters.emissaoInicio)
      }),
      ...(localFilters.emissaoFinal && {
        EMISSAO_FINAL: formatDateForApi(localFilters.emissaoFinal)
      }),
      ...(localFilters.envioInicio && {
        ENVIO_INICIO: formatDateForApi(localFilters.envioInicio)
      }),
      ...(localFilters.envioFinal && {
        ENVIO_FINAL: formatDateForApi(localFilters.envioFinal)
      }),
      ...(localFilters.operacao && { OPERACAO: localFilters.operacao }),
      ...(localFilters.valorMinimo && {
        VALOR_MIN: valorParaApi(localFilters.valorMinimo)
      }),
      ...(localFilters.valorMaximo && {
        VALOR_MAX: valorParaApi(localFilters.valorMaximo)
      })
    }

    if (filters.OPERACAO.length === 0) {
      delete filters.OPERACAO
    }

    despacho(mudarFiltros(filters))
  }

  const handleFuncaoEstadosRedux = () => {
    ColetaClickstream(1216)

    if (!temAlgumFiltroPreenchido(localFilters)) return

    const arrayFiltrosSimcard = montarArrayFiltrosBarra(
      localFilters,
      tiposPedidosOptions
    )

    formatFiltersToApply()
    despacho(realizouFiltro(true))
    despacho(filtrosArrayBarra(arrayFiltrosSimcard))
    despacho(revelarBarraFiltragem(true))
    despacho(recarregarBarraFiltragem(!recarregarBarraFiltragemValor))
    close()
  }

  const handleCleanFilters = () => {
    setLocalFilters(DEFAULT_FILTERS_VALUES)
    setFormResetKey((prev) => prev + 1)
    despacho(filtrosArrayBarra([]))
    despacho(revelarBarraFiltragem(false))
    despacho(mudarFiltros({ vazio: 'vazio' }))
  }

  return (
    <Modal
      width="950px"
      type="filtro"
      titulo="Filtros"
      disableConfirm={!temAlgumFiltroPreenchido(localFilters)}
      opened={opened}
      close={close}
      onConfirm={handleFuncaoEstadosRedux}
      handleCleanFilters={handleCleanFilters}
    >
      <S.Container>
        <S.Card>
          <div style={{ display: "flex", gap: "16px", width: "100%" }}>
            <S.Section>
              <S.SectionLabel>Data de emissão</S.SectionLabel>
              <S.FieldsRow key={`emissao-${formResetKey}`}>
                <DatePicker
                  prefixo="De:"
                  placeholder="dd/mm/aaaa"
                  value={localFilters.emissaoInicio}
                  onChange={(date) => {
                    handleLocalFiltroChange('emissaoInicio', date)
                  }}
                />
                <DatePicker
                  prefixo="Até:"
                  placeholder="dd/mm/aaaa"
                  minDate={localFilters.emissaoInicio}
                  value={localFilters.emissaoFinal}
                  onChange={(date) =>
                    handleLocalFiltroChange('emissaoFinal', date)
                  }
                />
              </S.FieldsRow>
            </S.Section>
            <S.Section>
              <S.SectionLabel>Data de envio</S.SectionLabel>
              <S.FieldsRow key={`envio-${formResetKey}`}>
                <DatePicker
                  prefixo="De:"
                  placeholder="dd/mm/aaaa"
                  value={localFilters.envioInicio}
                  onChange={(date) =>
                    handleLocalFiltroChange('envioInicio', date)
                  }
                />
                <DatePicker
                  prefixo="Até:"
                  placeholder="dd/mm/aaaa"
                  minDate={localFilters.envioInicio}
                  value={localFilters.envioFinal}
                  onChange={(date) =>
                    handleLocalFiltroChange('envioFinal', date)
                  }
                />
              </S.FieldsRow>
            </S.Section>
          </div>

          <div style={{ display: "flex", gap: "16px", width: "100%" }}>
            <S.Section>
              <MultiSelect
                label="Tipo"
                withSelectAll
                options={tiposPedidosOptions}
                value={localFilters.operacao}
                onChange={(value) => {
                  handleLocalFiltroChange('operacao', value)
                }}
              />
            </S.Section>

            <S.Section>
              <S.SectionLabel>Valor</S.SectionLabel>
              <S.FieldsRow>
                <Input
                  value={localFilters.valorMinimo}
                  placeholder="Valor mínimo"
                  type="text"
                  inputMode="decimal"
                  maxLength={14}
                  onChange={(e) => {
                    handleValorChange('valorMinimo', e.target.value)
                  }}
                />
                <Input
                  value={localFilters.valorMaximo}
                  placeholder="Valor máximo"
                  type="text"
                  inputMode="decimal"
                  maxLength={14}
                  onChange={(e) =>
                    handleValorChange('valorMaximo', e.target.value)
                  }
                />
              </S.FieldsRow>
            </S.Section>
          </div>
        </S.Card>
      </S.Container>
    </Modal>
  )
}
